<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAdminStore } from '@/stores/adminStore';
import Slider from '@/components/Slider.vue';
import PrimaryButton from '@/components/PrimaryButton.vue';

const adminStore = useAdminStore();
const hamburger = ref(null);
const navMenu = ref(null);
const overlay = ref(null);
const closeButton = ref(null);
const navLinks = ref([]); // Initialize as an empty array
const body = document.body;

const route = useRoute();

// Define ordering routes and their corresponding progress percentages
const orderingRoutes = {
  home: 0,
  'cleaning-standard': 33,
  summary: 66,
  success: 100,
};

// Compute whether to show progress bar
const showProgressBar = computed(() => {
  return Object.keys(orderingRoutes).includes(route.name);
});

// Compute progress percentage
const progress = computed(() => {
  return orderingRoutes[route.name] || 0;
});

function closeMenu() {
  hamburger.value.classList.remove('active');
  navMenu.value.classList.remove('active');
  overlay.value.classList.remove('active');
  body.style.overflow = '';
}

function toggleMenu() {
  const isActive = navMenu.value.classList.toggle('active');
  hamburger.value.classList.toggle('active');
  overlay.value.classList.toggle('active');
  body.style.overflow = isActive ? 'hidden' : '';
}

onMounted(() => {
  hamburger.value?.addEventListener('click', toggleMenu);
  closeButton.value?.addEventListener('click', closeMenu);
  overlay.value?.addEventListener('click', closeMenu);

  // Populate navLinks with actual DOM elements
  navLinks.value = Array.from(document.querySelectorAll('.nav-menu a'));
  navLinks.value.forEach(link => {
    link.addEventListener('click', closeMenu);
  });
});

onBeforeUnmount(() => {
  hamburger.value?.removeEventListener('click', toggleMenu);
  closeButton.value?.removeEventListener('click', closeMenu);
  overlay.value?.removeEventListener('click', closeMenu);

  navLinks.value.forEach(link => {
    link.removeEventListener('click', closeMenu);
  });
});
</script>


<template>
  <section class="bg-blue_primary duration-300 fixed lg:static header not-prose py-5 top-0 transition-all w-full z-[999] xl:px-5">    <div class="flex items-center justify-between max-w-screen-xl mx-auto px-3 xl:px-0">
      <div class="flex flex-row gap-2 items-center xl:mr-14 mr-2">
        <div ref="hamburger" class="block hamburger xl:hidden w-8 cursor-pointer">
          <span class="bar bg-white block h-0.5 mx-auto my-1.5 rounded-full w-5"></span>
          <span class="bar bg-white block h-0.5 mx-auto my-1.5 rounded-full w-5"></span>
          <span class="bar bg-white block h-0.5 mx-auto my-1.5 rounded-full w-5"></span>
        </div>
        <a href="https://easyspeak.nl/" class="mr-2">
          <img src="/images/SiteLogo.svg" class="h-7 w-auto xl:max-w-[180px] xl:h-[33px] xl:w-[180px]">
        </a>
      </div>
      
      <!-- Add mobile-only button in header (outside the menu) -->
      <!-- Add mobile-only button in header (outside the menu) -->
      <RouterLink to="/" class="block xl:hidden">
        <PrimaryButton text="Zarezerwuj tłumacza" class="px-4 py-3 text-sm font-semibold min-w-[180px] whitespace-nowrap" />
      </RouterLink>
      
      <div ref="overlay" class="bg-black duration-300 ease-in-out fixed inset-0 invisible nav-overlay opacity-0 pointer-events-none transition-opacity z-[1000]"></div>
      <nav ref="navMenu" class="bg-blue_primary fixed top-0 left-[-100%] h-full flex flex-col nav-menu overflow-auto w-9/12 transition-all duration-300 z-[1001] xl:bg-transparent xl:flex-row xl:items-center xl:justify-between xl:overflow-visible xl:static xl:w-full xl:h-auto xl:left-0">
        <div class="border-b border-gray_primary border-l-0 border-r-0 border-solid border-t-0 flex flex-row gap-5 items-center p-5 xl:hidden">
          <button ref="closeButton" class="close-menu">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 4.19531L21 20" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3 19.8047L21 4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <a href="#">
            <img src="/images/SiteLogo.svg" class="max-h-7 xl:max-w-[180px] xl:min-h-[33px] xl:min-w-[180px] xl:w-[180px]">
          </a>
        </div>
        <div class="flex flex-col xl:flex-row xl:gap-1 xl:items-center">
          <ul class="flex flex-col items-start xl:flex-row xl:gap-1 xl:w-full">
            <li class="w-full">
              <a href="https://easyspeak.nl/#opinie" class="border-b border-gray_primary border-l-0 border-r-0 border-solid border-t-0 btn-7 duration-300 flex font-medium font-sans items-center justify-between nav-link p-5 rounded-none text-center text-white hover:bg-opacity-10 hover:bg-white xl:border-b-0 xl:leading-6 xl:p-3 xl:px-6 xl:rounded-full xl:text-base">
                <span>Opinie</span>
                <div class="xl:hidden">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 5L12 10L7 15" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </a>
            </li>
            <li class="w-full">
              <a href="https://easyspeak.nl/pomoc/" class="border-b border-gray_primary border-l-0 border-r-0 border-solid border-t-0 btn-7 duration-300 flex font-medium font-sans items-center justify-between nav-link p-5 rounded-none text-center text-white hover:bg-opacity-10 hover:bg-white xl:border-b-0 xl:leading-6 xl:p-3 xl:px-6 xl:rounded-full xl:text-base">
                <span>Pomoc</span>
                <div class="xl:hidden">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 5L12 10L7 15" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </a>
            </li>
          </ul>
        </div>
        <div class="flex flex-col gap-4 items-center justify-center max-w-96 mx-auto px-3 py-6 w-full xl:flex-row xl:max-w-none xl:mx-0 xl:p-0 xl:w-fit">
          <!-- Mobile-only button for "Zarezerwuj tłumacza" inside menu can be removed -->
          <!-- <RouterLink to="/zamowienia/sprzatanie-standard" class="w-full xl:hidden mb-4">
            <PrimaryButton text="Zarezerwuj tłumacza" class="w-full" />
          </RouterLink> -->
          
          <a href="https://easyspeak.nl/tlumaczenie-dokumentow/" class="bg-transparent border border-gray_primary border-solid btn-7 duration-300 flex font-medium font-sans gap-2 group items-center justify-center leading-6 mb-2 px-6 py-4 rounded-full text-center text-white text-xs translation-button w-full hover:bg-opacity-10 hover:bg-white xl:mb-0 xl:mr-8 xl:text-base xl:w-max">
            <span>Tłumaczenie dokumentów</span>
            <p class="bg-green_primary font-Funnel_Sans font-semibold px-[6px] py-1 rounded-sm text-blue_primary text-xs">Soon</p>
          </a>
          <RouterLink v-if="!adminStore.isAdmin" to="/konto/zamowienia" class="bg-transparent border border-gray_primary border-solid btn-7 duration-300 flex font-medium font-sans gap-2 group items-center justify-center leading-6 min-w-0 px-6 py-4 rounded-full text-center text-white text-xs w-full hover:bg-opacity-10 hover:bg-white xl:text-base xl:w-max">
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
              <path d="M3.56104 10.0132C3.55707 10.0166 3.55349 10.0204 3.54951 10.0242C2.23264 11.27 1.49446 12.2079 1.50003 13.9744C1.50003 15.9395 5.10621 17 8.5 17C11.8938 17 15.5 15.9395 15.5 13.9744C15.5055 12.1958 14.757 11.2473 13.4238 10" stroke="white" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"/>
              <path d="M8.5 9C10.7093 9 12.5 7.20929 12.5 5C12.5 2.79071 10.7093 1 8.5 1C6.29071 1 4.5 2.79071 4.5 5C4.5025 7.20821 6.29179 8.9975 8.5 9Z" stroke="white" stroke-width="1.5" stroke-miterlimit="10"/>
            </svg>
            <span>Moje konto</span>
          </RouterLink>
          <RouterLink v-else to="/admin/zamowienia" class="bg-green_primary btn-6 duration-300 flex font-sans font-semibold gap-2 group items-center justify-center leading-6 px-6 py-4 rounded-full text-blue_primary text-center text-xs w-full hover:bg-green_primary_hover xl:text-base xl:w-max">
            <span>Konto admina</span>
          </RouterLink>
          
          <!-- Desktop-only button for "Zarezerwuj tłumacza" -->
          <RouterLink to="/" class="w-max hidden xl:block">
            <PrimaryButton text="Zarezerwuj tłumacza" />
          </RouterLink>
        </div>
      </nav>
    </div>
  </section>
</template>

<style scoped>
.nav-menu.active {
  left: 0;
}

.nav-overlay.active {
  display: block;
  opacity: 0.5;
  pointer-events: auto;
  visibility: visible;
}

@media (min-width: 1280px) {
  .nav-menu {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>